/**
 * IssuesSection - Issues list with modern styling
 *
 * Extracted from ModernResultsFull.tsx for Phase 3 Mobile Optimization.
 * Supports truncation (Phase 3.3) and accessibility.
 */

import { View, Pressable, StyleSheet } from 'react-native';
import { TierBody, TierHeadline } from '@dramadetector/ui';
import type { DramaIssue } from '@dramadetector/shared';

import { captureEvent } from '../../../lib/analytics';
import { useSectionTruncation, TRUNCATION_DEFAULTS } from '../hooks';
import { sharedStyles, SEVERITY_COLORS } from '../sharedStyles';

/** Format any issue type string (camelCase, kebab-case, Title Case, etc.) to Title Case display */
function formatIssueType(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase → spaced
    .replace(/[-_]+/g, ' ')                   // kebab/snake → spaced
    .replace(/\b\w/g, c => c.toUpperCase());  // Title Case
}

interface IssueItemProps {
  issue: DramaIssue;
  isLast: boolean;
}

/**
 * Individual issue item display
 */
function IssueItem({ issue, isLast }: IssueItemProps) {
  const severityColor = SEVERITY_COLORS[issue.severity] || SEVERITY_COLORS.medium;

  return (
    <View style={[sharedStyles.issueItem, isLast && sharedStyles.issueItemLast]}>
      {/* Severity Badge */}
      <View
        style={[
          sharedStyles.severityBadge,
          { backgroundColor: severityColor.bg },
        ]}
      >
        <TierBody size="xs" style={{ color: severityColor.text, fontWeight: '600' }}>
          {(issue.severity || 'medium').toUpperCase()}
        </TierBody>
      </View>

      {/* Issue Type & Description */}
      <TierBody size="sm" color="text-gray-800" style={{ fontWeight: '600', marginBottom: 4 }}>
        {formatIssueType(issue.raw_type || issue.type || 'Issue')}
      </TierBody>
      <TierBody size="sm" color="text-gray-600">
        {issue.description}
      </TierBody>

      {/* Snippet */}
      {issue.snippet && (
        <View style={sharedStyles.snippetBox}>
          <TierBody size="xs" color="text-gray-500" style={{ fontStyle: 'italic' }}>
            "{issue.snippet}"
          </TierBody>
        </View>
      )}
    </View>
  );
}

export interface IssuesSectionProps {
  /** List of issues to display */
  issues: DramaIssue[];
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
  /** Maximum items before truncation (default: 3) */
  maxVisible?: number;
}

/**
 * Issues section with header and list
 * Supports truncation with "Show more/less" toggle
 */
export function IssuesSection({
  issues,
  t,
  testID,
  maxVisible = TRUNCATION_DEFAULTS.issues,
}: IssuesSectionProps) {
  const { showAll, toggle, isTruncated, visibleCount, hiddenCount } = useSectionTruncation({
    totalItems: issues.length,
    maxVisible,
  });

  if (!issues || issues.length === 0) {
    return null;
  }

  const visibleIssues = showAll ? issues : issues.slice(0, visibleCount);

  const handleToggle = () => {
    captureEvent('Section_Toggle', {
      section: 'issues',
      action: showAll ? 'collapse' : 'expand',
      total_items: issues.length,
      visible_items: visibleCount,
    });
    toggle();
  };

  return (
    <View style={sharedStyles.glassCard} testID={testID}>
      <View style={sharedStyles.sectionHeader}>
        <TierHeadline size="lg" weight="semibold" color="text-gray-800" as="h3">
          {t('home.modern.issues_detected', { defaultValue: 'Issues Detected' })}
        </TierHeadline>
      </View>
      {visibleIssues.map((issue, index) => (
        <IssueItem
          key={`issue-${index}`}
          issue={issue}
          isLast={index === visibleIssues.length - 1 && !isTruncated}
        />
      ))}
      {/* Show more/less toggle */}
      {isTruncated && (
        <Pressable
          onPress={handleToggle}
          style={styles.toggleButton}
          accessibilityRole="button"
          accessibilityLabel={
            showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })
          }
          testID={`${testID}-toggle`}
        >
          <TierBody size="sm" style={styles.toggleText}>
            {showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })}
          </TierBody>
        </Pressable>
      )}
    </View>
  );
}

/**
 * StyleSheet - WCAG AAA compliant
 * - Touch target: min 44x44 points
 * - Contrast: purple-800 #5B21B6 on white = 9.4:1
 */
const styles = StyleSheet.create({
  toggleButton: {
    paddingVertical: 14,
    minHeight: 44, // WCAG touch target
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleText: {
    color: '#5B21B6', // purple-800 (WCAG AAA: 9.4:1 contrast)
    fontWeight: '600',
  },
});
